/**
*****************************************
*										*
* NAGARRO  - CSX - CONFIDENTIAL         *
*										*
*****************************************
*/



import * as _ from 'lodash';

import { AppUtilityService } from '../../core/services/app-utility.service';
import { BehaviorSubject ,  Observable } from 'rxjs';
import { ConfigService } from '../../core/services/config.service';
import { DataStoreService } from '../../core/services/datastore.service';
import { EditModeService } from './../../core/services/edit-mode.service';
import { HistoryOps } from '../../core/model/history-ops.model';
import { HttpStatus } from '../../core/model/http-status-enum.model';
import { Injectable } from '@angular/core';
import { LmsLoaderService } from './../../core/services/lms-loader.service';
import { LmsLoggerService } from '../../core/services/lms-logger.service';
import { LocoFeatureList } from '../../core/model/loco-feature-list-model';
import { MessageDetails } from '../../core/model/message-details.model';
import { MessageHandlerService } from '../../core/services/message-handler.service';
import { PopupService } from '../../core/services/popup.service';
import { Router } from '@angular/router';
import { StaticDataStoreService } from './../../core/services/static-datastore.service';
import { Tab } from '../../core/model/tab.model';
import { TabData } from '../../core/model/tab-data.model';
import { TrainData } from '../../core/model/train-data.model';
import { TrainHistory } from '../../core/model/train-history.model';
import { HttpClient } from '@angular/common/http';

/**
 * This TrainDataService is created for fetch train related information from http service call.
 * @export
 * @class TrainDataService
 */
@Injectable()
export class TrainHistoryService {

    commonopsPicklist: any;
    loadData: Observable<TrainHistory>;
    private loadDataBS: BehaviorSubject<TrainHistory>;
    private RESOURCE_URL = '/trains';
    private configData = this.configService.getConfigData();
    locoFeatureListArr: Array<LocoFeatureList>;

    /**
     * Creates an instance of TrainHistoryService and inject HttpClient, ConfigService, DataStoreService,
     * LmsLoggerService.
     * @param {HttpClient} http
     * @param {ConfigService} configService
     * @param {DataStoreService} dataStoreService
     * @param {LmsLoggerService} lmsLoggerService
     * @memberOf TrainHistoryService
     */
    constructor(private http: HttpClient,
        private configService: ConfigService,
        private dataStoreService: DataStoreService,
        private lmsLoggerService: LmsLoggerService,
        private router: Router,
        private lmsLoaderService: LmsLoaderService,
        private messageHandlerService: MessageHandlerService,
        private staticDataStoreService: StaticDataStoreService,
        private popupService: PopupService,
        private editModeService: EditModeService,
        private appUtilService: AppUtilityService) {
        this.loadDataBS = <BehaviorSubject<TrainHistory>>new BehaviorSubject({});
        this.loadData = this.loadDataBS.asObservable();
    }

    private isNumeric(value: string): boolean {
        const v = value.match(/^\d+$/);
        if (v) {
            if (v.toString() === value) {
                return true;
            }
        }
        return false;
    }

    isValidTrainId(value: string): boolean {
        let flag = false;
        if (value && value.length === 6) {
            if (!this.isNumeric(value.charAt(0))) {
                const remainingStr = value.substring(1, value.length);
                if (this.isNumeric(remainingStr)) {
                    flag = true;
                }
            }
        }
        return flag;
    }

    private resetDefaultPageMode() {
        this.popupService.setPageMode('');
        this.popupService.setSubmitBtnId('');
    }

    /**
     * This method invoked when we required to fetch data either from http service
     * or datastore.
     * @param {Tab} tab
     * @param {boolean} isRefresh
     * @memberOf TrainHistoryService
     */
    load(tab: Tab, historyOps: HistoryOps, fromOtherOperation?: boolean, messageText?: string) {
        this.resetDefaultPageMode();
        const foundInDataStore = false;
        historyOps = this.checkForHistoryOpsValidity(historyOps);
        if (tab && tab.tabSearchParameter) {
            if (tab.tabSearchParameter.searchText) {
                tab.tabSearchParameter.searchText = tab.tabSearchParameter.searchText.trim();
            } else {
                this.lmsLoaderService.hideLoader();
                return;
            }
            if (tab.subTypeDetail.history.status === 'error') {
                this.lmsLoaderService.hideLoader();
                return;
            }
            if (!this.isValidTrainId(tab.tabSearchParameter.searchText)) {
                const lTrainHistory = <TrainHistory>({
                    httpStatus: 400,
                    errorMessage: this.configService.getConfigData().messages.invalidFormatTrain
                });
                this.loadDataBS.next(lTrainHistory);
                this.lmsLoggerService.logError('TrainHistoryService => load()',
                    this.configService.getConfigData().messages.invalidFormatTrain);
                this.lmsLoaderService.hideLoader();
            } else {
                this.processServiceCallForRefresh(foundInDataStore, tab, fromOtherOperation, historyOps, messageText);
            }
        } else {
            this.lmsLoaderService.hideLoader();
            this.loadDataBS.next(null);
        }
    }

    private processServiceCallForRefresh(foundInDataStore: boolean, tab: Tab, fromOtherOperation: boolean, historyOps: any, messageText?: string) {
        if (tab.subTypeDetail.history.status !== 'refresh') {
            const tabData = this.dataStoreService.getValue(tab);
            let trainHistory;
            if (tabData) {
                trainHistory = <TrainHistory>(tabData.data[tab.subType]);
                foundInDataStore = true;
                this.loadDataBS.next(trainHistory);
            }
            this.lmsLoaderService.hideLoader();
        } else {
            foundInDataStore = false;
        }
        if (!foundInDataStore) {
            this.lmsLoaderService.showLoader();
            const opr = 'INQT';
            this.lmsLoggerService.logInfo('TrainHistoryService=>load service call starts');
            this.http.get(
                `${this.configService.getConfigData().urls.API_URL}${this.RESOURCE_URL}?id=${tab.tabSearchParameter.searchText}&opr=${opr}&d=${historyOps.days}&dt=${historyOps.displayType}`)
                .subscribe(data => {
                    this.subscribeData(data, tab, fromOtherOperation, messageText);
                },
                error => {
                    const lTrainData = <TrainData>({
                        httpStatus: error.status,
                        errorMessage: fromOtherOperation ?
                            messageText + ', but an error has occurred while refreshing the page.' : this.appUtilService.getHttpErroMessage(error, false)
                    });
                    this.loadDataBS.next(lTrainData);
                    this.lmsLoggerService.logError('TrainDataService => load()', this.appUtilService.getHttpErroMessage(error, false));
                    this.lmsLoaderService.hideLoader();
                });
        }
    }

    private subscribeData(data: any, tab: Tab, fromOtherOperation: boolean, messageText?: string) {
        if (data && data.priorityCode) {
            const pCode = parseInt(data.priorityCode, 10);
            if (pCode >= 1 && pCode <= 4) {
                const lTrainHistory: TrainHistory = {};
                lTrainHistory.commonHistory = (data.history);
                lTrainHistory.trainIdentifier = tab.tabSearchParameter.searchText;
                const tabData = <TabData>({
                    id: tab.id,
                    subType: tab.subType
                });

                lTrainHistory.lastRefreshTime = data.refreshTime;
                lTrainHistory.refreshTime = data.refreshTime;
                lTrainHistory.httpStatus = HttpStatus.OK;
                lTrainHistory.tabId = tab.id;
                lTrainHistory.errorMessage = '';
                tab.subTypeDetail.history.message = '';
                tabData.data = tabData.data ? tabData.data : {};
                tabData.data[tab.subType] = lTrainHistory;

                this.dataStoreService.putValue(tab, tabData);
                this.loadDataBS.next(lTrainHistory);
                this.lmsLoggerService.logInfo('TrainHistoryService => load()',
                    `Train History Fetch Successfull for ${JSON.stringify(tab)}`);

                if (fromOtherOperation) {
                    this.messageHandlerService.handleMessage(<MessageDetails>({
                        type: this.configData.message.type.client,
                        level: this.configData.message.level.success,
                        target: this.configData.message.target.page,
                        message: messageText
                    }));
                }
            } else {
                const lTrainData = <TrainData>({
                    httpStatus: 400,
                    errorMessage: fromOtherOperation ? messageText + ', but ' + data.messageText : data.messageText
                });
                this.loadDataBS.next(lTrainData);
                this.lmsLoggerService.logError('TrainDataService => load()', data.messageText);
            }
            this.lmsLoaderService.hideLoader();
        }
        this.lmsLoggerService.logInfo('TrainHistoryService=>load service call ends');
    }

    private checkForHistoryOpsValidity(historyOps: HistoryOps) {
        if (historyOps) {
            if (!historyOps.days) {
                historyOps.days = 5;
            }
            if (!historyOps.displayType) {
                historyOps.displayType = ' ';
            }
        } else {
            historyOps = {
                days: 5,
                displayType: ' '
            };
        }
        return historyOps;
    }

    /**
     * @memberOf TrainDataService
     */
    cleanBehaviourSubject() {
        this.loadDataBS.next(null);
    }
}
